import React, { useEffect, useState } from 'react';
import {
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';

const CREATE_NEW = '__create_new_resource_group__';

export const AzureResourceGroupPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { formData, onChange, rawErrors, required, schema } = props;
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [resourceGroups, setResourceGroups] = useState<string[]>([]);
  const [createNew, setCreateNew] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const ownerRefs = new Set(
          identity.ownershipEntityRefs
            .filter(ref => parseEntityRef(ref).kind.toLowerCase() === 'group')
            .map(ref => stringifyEntityRef(parseEntityRef(ref)).toLowerCase()),
        );
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': 'azure-databricks-app',
          },
        });
        const ownedApps = response.items.filter(entity => {
          const owner = String(entity.spec?.owner ?? '').toLowerCase();
          return ownerRefs.has(stringifyEntityRef(parseEntityRef(owner)).toLowerCase());
        });

        const groups = new Set<string>();
        for (const app of ownedApps) {
          const directGroup = String(app.metadata.resourceGroup ?? '').trim();
          if (directGroup) {
            groups.add(directGroup);
            continue;
          }

          for (const environmentRef of (app.spec?.dependsOn as string[] | undefined) ?? []) {
            const environment = await catalogApi.getEntityByRef(environmentRef);
            for (const providerRef of
              (environment?.spec?.dependsOn as string[] | undefined) ?? []) {
              const provider = await catalogApi.getEntityByRef(providerRef);
              const resourceGroup = String(provider?.metadata.resourceGroup ?? '').trim();
              if (resourceGroup) groups.add(resourceGroup);
            }
          }
        }

        const sorted = [...groups].sort((a, b) => a.localeCompare(b));
        setResourceGroups(sorted);
        if (formData && !sorted.includes(formData)) setCreateNew(true);
      } catch {
        setResourceGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [catalogApi, identityApi, formData]);

  const label = String(schema.title ?? 'Resource Group');
  const description = String(
    schema.description ??
      'Select a resource group used by one of your group-owned applications, or create a new one.',
  );
  const hasError = Boolean(rawErrors?.length);

  return (
    <>
      <FormControl
        fullWidth
        margin="normal"
        required={required}
        error={hasError}
        disabled={loading}
      >
        <InputLabel>{label}</InputLabel>
        <Select
          value={createNew ? CREATE_NEW : formData ?? ''}
          onChange={event => {
            const value = String(event.target.value);
            if (value === CREATE_NEW) {
              setCreateNew(true);
              onChange('');
            } else {
              setCreateNew(false);
              onChange(value);
            }
          }}
        >
          {resourceGroups.map(resourceGroup => (
            <MenuItem key={resourceGroup} value={resourceGroup}>
              {resourceGroup}
            </MenuItem>
          ))}
          <MenuItem value={CREATE_NEW}>Create a new resource group</MenuItem>
        </Select>
        <FormHelperText>
          {loading ? 'Loading resource groups from your applications...' : description}
        </FormHelperText>
      </FormControl>
      {createNew && (
        <TextField
          fullWidth
          required={required}
          margin="normal"
          label="New Resource Group Name"
          value={formData ?? ''}
          error={hasError}
          helperText="The deployment pipeline will create this resource group when it does not exist."
          onChange={event => onChange(event.target.value)}
          inputProps={{ pattern: '^[a-zA-Z0-9._\\-()]+$' }}
        />
      )}
    </>
  );
};
